<template>
  <div class="register-state-notice" :role="role">
    <div
      class="register-state-notice__icon"
      :class="{ 'register-state-notice__icon--error': tone === 'error' }"
      aria-hidden="true">
      <v-icon size="28">{{ icon }}</v-icon>
    </div>
    <div class="register-state-notice__body">
      <span v-if="eyebrow" class="register-state-notice__eyebrow">{{ eyebrow }}</span>
      <h3>{{ title }}</h3>
      <p><slot /></p>
      <dl v-if="showModeDefinitions" class="register-state-notice__modes">
        <div>
          <dt><v-icon size="16">mdi-domain</v-icon>Shared</dt>
          <dd>Rows sit at Facility Group level. Every member Facility sees every row.</dd>
        </div>
        <div>
          <dt><v-icon size="16">mdi-map-marker-outline</v-icon>Separated</dt>
          <dd>Each row belongs to one Facility. A Facility sees only its own rows.</dd>
        </div>
      </dl>
      <div v-if="$slots.actions" class="register-state-notice__actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<script>
  /**
   * Register grid state card (showcase B `.gate`): Configuration required, Waiting for the first
   * import, load failures and empty results. Calm states use role="status" and a neutral icon;
   * only a real failure uses role="alert" and the error tone.
   */
  export default {
    name: 'RegisterTableStateNotice',
    props: {
      icon: { type: String, required: true },
      title: { type: String, required: true },
      eyebrow: { type: String, default: '' },
      tone: { type: String, default: 'neutral', validator: value => ['neutral', 'error'].includes(value) },
      role: { type: String, default: 'status', validator: value => ['status', 'alert'].includes(value) },
      showModeDefinitions: { type: Boolean, default: false },
    },
  };
</script>

<style lang="scss" scoped>
  /* Neutral surface; severity is the icon's job. --register-muted is the readable secondary grey the
     register surfaces share (the theme's text_secondary is ~1.6:1 on white). */
  .register-state-notice {
    --register-muted: #59635f;
    --register-state-icon-bg: #eef1f0;

    display: flex;
    align-items: flex-start;
    gap: var(--ds-space-md, 16px);
    max-width: 1280px;
    margin: var(--ds-space-lg, 24px) auto;
    padding: 32px;
    border: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.12));
    border-radius: var(--ds-radius-lg, 12px);
    background: var(--v-bg2-base);
    color: var(--v-text-base);
    text-align: left;
    white-space: normal;

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      flex: 0 0 48px;
      border-radius: 50%;
      background: var(--register-state-icon-bg);

      .v-icon { color: var(--register-muted); }

      &--error .v-icon { color: var(--v-error-base); }
    }

    &__body {
      display: flex;
      min-width: 0;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }

    &__eyebrow {
      color: var(--register-muted);
      font-size: var(--ds-font-caption, 12px);
      font-weight: 600;
      letter-spacing: 0.02em;
    }

    h3 {
      margin: 0;
      font-family: var(--ds-font-family-heading);
      font-size: var(--ds-font-subtitle, 16px);
      font-weight: 600;
    }

    p {
      max-width: 70ch;
      margin: 0;
      color: var(--register-muted);
      font-size: var(--ds-font-body-sm, 13px);
      line-height: 1.55;

      ::v-deep b {
        color: var(--v-text-base);
        font-weight: 600;
      }
    }

    &__modes {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 260px));
      gap: 10px;
      margin: 10px 0 2px;

      > div {
        padding: 10px 12px;
        border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08));
        border-radius: var(--ds-radius-md, 8px);
        background: var(--ds-surface-elevated, #fff);
      }

      dt {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: var(--ds-font-body-sm, 13px);
        font-weight: 600;

        .v-icon { color: var(--v-primary-base); }
      }

      dd {
        margin: 3px 0 0;
        color: var(--register-muted);
        font-size: var(--ds-font-caption, 12px);
        line-height: 1.45;
      }
    }

    &__actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--ds-space-sm, 8px);
      margin-top: 10px;
    }
  }

  .theme--dark .register-state-notice {
    --register-muted: #c8c8c8;
    --register-state-icon-bg: var(--v-bg3-base);

    &__icon--error .v-icon { color: #ef9a9a; }

    &__modes {
      > div { background: var(--v-bg1-base); }

      dt .v-icon { color: var(--v-secondary-base); }
    }
  }

  @media (max-width: 900px) {
    .register-state-notice__modes { grid-template-columns: 1fr; }
  }

  @media (max-width: 600px) {
    .register-state-notice {
      flex-direction: column;
      padding: 20px;
    }
  }
</style>
